import { memo, useMemo } from 'react'

import { formatInt, formatPct, pctOf } from '../../lib/format.ts'
import type { AgentVersion } from '../../lib/sse/types.ts'
import { useElementSize } from './useElementSize.ts'

const HEIGHT = 18

/** Sequential ramp — magnitude, not category. Versions past the fifth share the tail. */
const RAMP = ['var(--c-seq-2)', 'var(--c-seq-3)', 'var(--c-seq-4)', 'var(--c-seq-5)'] as const

/**
 * Agent versions as one stacked bar.
 *
 * The operational question is not "how many versions are deployed" but "how much
 * of the fleet is sitting on a build with a known issue". So the ramp encodes
 * magnitude and the known-issue segments break out of it in the warning colour —
 * the only categorical distinction here is one that means something.
 */
export const VersionBar = memo(function VersionBar({ versions }: { versions: AgentVersion[] }) {
  const [ref, size] = useElementSize<HTMLDivElement>()
  const width = size.width

  const { segments, total } = useMemo(() => {
    const sorted = [...versions].sort((a, b) => b.count - a.count)
    const sum = sorted.reduce((acc, version) => acc + version.count, 0)
    let offset = 0
    const built = sorted.map((version, index) => {
      const span = pctOf(version.count, sum)
      const segment = {
        version,
        left: offset,
        width: span,
        fill: version.has_known_issue
          ? 'var(--c-warn)'
          : (RAMP[Math.min(index, RAMP.length - 1)] ?? 'var(--c-seq-5)'),
      }
      offset += span
      return segment
    })
    return { segments: built, total: sum }
  }, [versions])

  const label = segments
    .map(
      (segment) =>
        `${segment.version.version}: ${formatInt(segment.version.count)} agentes${
          segment.version.has_known_issue ? ' (issue conhecida)' : ''
        }`,
    )
    .join('; ')

  return (
    <div ref={ref} className="live-chart w-full">
      {width > 0 && total > 0 && (
        <svg width={width} height={HEIGHT} role="img" aria-label={`Distribuição de versões. ${label}.`}>
          {segments.map((segment) => (
            <rect
              key={segment.version.version}
              x={(segment.left / 100) * width}
              y={0}
              width={Math.max(1, (segment.width / 100) * width - 1)}
              height={HEIGHT}
              fill={segment.fill}
              shapeRendering="crispEdges"
            />
          ))}
        </svg>
      )}
    </div>
  )
})

/** Legend rows. Kept as text so the numbers are selectable and screen-readable. */
export function VersionLegend({ versions }: { versions: AgentVersion[] }) {
  const total = versions.reduce((acc, version) => acc + version.count, 0)
  const sorted = [...versions].sort((a, b) => b.count - a.count)

  return (
    <ul className="mt-2 space-y-1">
      {sorted.map((version, index) => (
        <li key={version.version} className="flex items-baseline justify-between gap-2 text-sm">
          <span className="flex min-w-0 items-center gap-1.5">
            <span
              aria-hidden="true"
              className="inline-block size-1.5 shrink-0 rounded-xs"
              style={{
                backgroundColor: version.has_known_issue
                  ? 'var(--c-warn)'
                  : (RAMP[Math.min(index, RAMP.length - 1)] ?? 'var(--c-seq-5)'),
              }}
            />
            <span className="truncate font-mono tnum text-fg-secondary">{version.version}</span>
            {version.has_known_issue && (
              <span className="shrink-0 text-micro uppercase tracking-label text-warn">
                issue conhecida
              </span>
            )}
          </span>
          <span className="shrink-0">
            <span className="live-value font-mono text-fg">{formatInt(version.count)}</span>
            <span className="ml-1.5 live-value font-mono text-fg-muted">
              {formatPct(pctOf(version.count, total), 0)}
            </span>
          </span>
        </li>
      ))}
    </ul>
  )
}
